const OPENID_STATE_COOKIE = "oidc.state";
const OPENID_CODE_VERIFIER_COOKIE = "oidc.code-verifier";

function randomIdentifier(length: number) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;

  let result = '';
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

function randomString() {
  var arrayRand = new Uint32Array(28)
  window.crypto.getRandomValues(arrayRand)

  var resultArr = []

  for (var index = 0; index < arrayRand.length; index++) {
    resultArr.push(('0' + arrayRand[index]!.toString(16)).substr(-2));
  }

  return resultArr.join('')
}

function sha256(plain: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest("SHA-256", data);
}

function base64UrlEncode(a: ArrayBuffer) {
  var str = "";
  var bytes = new Uint8Array(a);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    str += String.fromCharCode(bytes[i]!);
  }
  return btoa(str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function generateCodeChallengeFromVerifier(v: string) {
  var hashed = await sha256(v);
  var base64encoded = base64UrlEncode(hashed);
  return base64encoded;
}

function encodeQuery(obj: any) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
}

export async function oidcLoginUrl(clientId: string, autorizationEndpoint: string, redirectUri: string) {
  const keycloakState = useCookie(OPENID_STATE_COOKIE)
  const keycloakCodeVerifier = useCookie(OPENID_CODE_VERIFIER_COOKIE)

  if (!keycloakState.value) {
    keycloakState.value = randomIdentifier(10);
  }

  if (!keycloakCodeVerifier.value) {
    keycloakCodeVerifier.value = randomString()
  }

  let codeChallenge = await generateCodeChallengeFromVerifier(keycloakCodeVerifier.value);

  let args = {
    "protocol": "oauth2",
    "response_type": "code",
    "accessType": "online",
    "client_id": clientId,
    "redirect_uri": redirectUri,
    "scope": "openid profile email",
    "code_challenge_method": "S256",
    "code_challenge": codeChallenge,
    "state": keycloakState.value,
  }

  return autorizationEndpoint + "?" + encodeQuery(args);
}

export async function oidcAccessToken(clientId: string, tokenEndpoint: string, redirectUri: string, storeTokens = false): Promise<boolean> {
  const route = useRoute()

  const keycloakState = useCookie(OPENID_STATE_COOKIE)
  const keycloakCodeVerifier = useCookie(OPENID_CODE_VERIFIER_COOKIE)

  let code = route.query["code"]
  let stateParam = route.query["state"]

  if (keycloakState.value != stateParam) {
    keycloakState.value = null
    keycloakCodeVerifier.value = null
    return false;
  }

  let args = {
    "code": code,
    "code_verifier": keycloakCodeVerifier.value,
    "client_id": clientId,
    "redirect_uri": redirectUri,
    "response_type": "code",
    "audience": "",
    "grant_type": "authorization_code",
  }

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: encodeQuery(args)
  })


  keycloakState.value = null
  keycloakCodeVerifier.value = null

  if (!response.ok) {
    return false
  }

  const result = await response.json()


  const access_token = result.access_token
  const refresh_token = result.refresh_token

  if (result.session_state) {
    sessionStorage.setItem("oidc-session-state", result.session_state)
  }

  if (result.id_token) {
    sessionStorage.setItem("oidc-id-token", result.id_token)
  }

  if (storeTokens && access_token) {
    sessionStorage.setItem("oidc-auth-token", access_token)
  }

  if (storeTokens && refresh_token) {
    sessionStorage.setItem("oidc-refresh-token", refresh_token)
  }

  const createSession = await fetch("/auth", {
    method: 'POST',
    headers: {
      "oidc-auth-token": access_token,
      "oidc-refresh-token": refresh_token
    }
  })

  if (!createSession.ok) {
    return false
  }

  return true
}

export async function oidcRefreshToken(clientId: string, tokenEndpoint: string): Promise<boolean> {
  let refreshToken = sessionStorage.getItem("oidc-refresh-token");
  if (!refreshToken) return false

  let args = {
    "refresh_token": refreshToken,
    "client_id": clientId,
    "grant_type": "refresh_token",
  }

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: encodeQuery(args)
  })

  const result = await response.json()
  const access_token = result.access_token

  if (result.session_state) {
    sessionStorage.setItem("oidc-session-state", result.session_state)
  }

  if (result.id_token) {
    sessionStorage.setItem("oidc-id-token", result.id_token)
  }

  if (access_token) {
    sessionStorage.setItem("oidc-auth-token", access_token)
  }

  if (result.refresh_token) {
    sessionStorage.setItem("oidc-refresh-token", result.refresh_token)
  }

  await fetch('/auth?logout=1', {
    method: 'HEAD'
  })

  const createSession = await fetch("/auth", {
    method: 'POST',
    headers: {
      "oidc-auth-token": access_token
    }
  })

  if (!createSession.ok) {
    return false
  }

  return true
}

export async function oidcLogout(clientId: string, endSsessionEndpoint: string) {
  const idTokenHint = sessionStorage.getItem("oidc-id-token");

  const hintQuery =
    idTokenHint && idTokenHint.trim() !== ""
      ? `&id_token_hint=${encodeURIComponent(idTokenHint)}`
      : "";

  window.location.assign(
    `${endSsessionEndpoint}?post_logout_redirect_uri=${encodeURIComponent(location.origin)}&client_id=${clientId}${hintQuery}`
  );
}